import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import { ArrowRight, ChevronDown, ChevronUp, Send } from 'lucide-react';

const ContactPage = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast({
      title: language === 'nl' ? 'Verstuurd!' : 'Sent!',
      description: language === 'nl' 
        ? 'We reageren binnen 24 uur.'
        : 'We\'ll respond within 24 hours.'
    });
    setFormData({ name: '', email: '', subject: '', message: '' });
    setSending(false);
  };

  const contactMethods = [
    {
      label: 'Email',
      value: 'support@swipejewoning.nl',
      subtitle: language === 'nl' ? 'Antwoord binnen 24u' : 'Response within 24h'
    },
    {
      label: language === 'nl' ? 'Kantoor' : 'Office',
      value: 'Amsterdam',
      subtitle: 'Nederland'
    },
    {
      label: language === 'nl' ? 'Uren' : 'Hours',
      value: '9:00 - 18:00',
      subtitle: language === 'nl' ? 'Ma - Vr' : 'Mon - Fri'
    }
  ];

  const faqs = [
    {
      question: language === 'nl' ? 'Hoe reset ik mijn wachtwoord?' : 'How do I reset my password?',
      answer: language === 'nl'
        ? 'Ga naar inloggen → "Wachtwoord vergeten" → Check je email'
        : 'Go to login → "Forgot password" → Check your email'
    },
    {
      question: language === 'nl' ? 'Wat kost het abonnement?' : 'What does the subscription cost?',
      answer: language === 'nl'
        ? '€4,99/week. Onbeperkt swipen. Opzeggen wanneer je wilt.'
        : '€4.99/week. Unlimited swiping. Cancel anytime.'
    },
    {
      question: language === 'nl' ? 'Hoe meld ik een nep woning?' : 'How do I report a fake listing?',
      answer: language === 'nl'
        ? 'Tap de "..." op de woning → "Rapporteer" → Wij checken binnen 2 uur'
        : 'Tap "..." on the listing → "Report" → We check within 2 hours'
    },
    {
      question: language === 'nl' ? 'Kan ik mijn account verwijderen?' : 'Can I delete my account?',
      answer: language === 'nl'
        ? 'Ja. Profiel → Instellingen → Scroll naar beneden → "Account verwijderen"'
        : 'Yes. Profile → Settings → Scroll down → "Delete account"'
    },
    {
      question: language === 'nl' ? 'Waarom krijg ik geen matches?' : 'Why am I not getting matches?',
      answer: language === 'nl'
        ? 'Tips: Compleet profiel invullen, budget realistisch houden, meerdere steden selecteren, dagelijks swipen'
        : 'Tips: Complete your profile, keep budget realistic, select multiple cities, swipe daily'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F7]" data-testid="contact-page">
      <Header />
      <Toaster />
      
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative py-24 px-4 overflow-hidden bg-[#1F3A5F]">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 right-20 w-80 h-80 bg-[#2E7D32] rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <span className="bg-white/10 text-white text-xs font-black tracking-widest px-4 py-2 rounded-full uppercase backdrop-blur-sm">
                Support
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-none">
              {language === 'nl' ? 'Hulp nodig?' : 'Need help?'}
            </h1>
            <p className="text-xl text-white/70 max-w-xl mx-auto">
              {language === 'nl' 
                ? 'Check eerst de FAQ. Nog steeds vast? Stuur ons een bericht.'
                : 'Check the FAQ first. Still stuck? Send us a message.'}
            </p>
          </div>
        </section>

        {/* Contact methods - Simple */}
        <section className="bg-white border-b border-[#E5E5E5]">
          <div className="max-w-4xl mx-auto px-4 py-10">
            <div className="grid md:grid-cols-3 gap-8">
              {contactMethods.map((method, i) => (
                <div key={i} className="text-center md:text-left">
                  <div className="text-xs font-black text-[#9E9E9E] tracking-widest uppercase mb-2">
                    {method.label}
                  </div>
                  <div className="text-xl font-bold text-[#2A2A2A]">{method.value}</div>
                  <div className="text-[#9E9E9E] text-sm">{method.subtitle}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main content */}
        <div className="max-w-5xl mx-auto px-4 py-16">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* FAQ */}
            <div>
              <div className="mb-8">
                <span className="text-[#2E7D32] font-black text-sm tracking-widest uppercase">FAQ</span>
                <h2 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mt-2">
                  {language === 'nl' ? 'Veelgestelde vragen' : 'Frequently asked'}
                </h2>
              </div>
              
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div 
                    key={i} 
                    className="bg-white rounded-2xl border-2 border-[#E5E5E5] overflow-hidden hover:border-[#2E7D32]/50 transition-colors"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full p-5 flex items-center justify-between text-left"
                    >
                      <span className="font-bold text-[#2A2A2A] pr-4">{faq.question}</span>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        openFaq === i ? 'bg-[#2E7D32] text-white' : 'bg-[#F7F7F7] text-[#9E9E9E]'
                      }`}>
                        {openFaq === i ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-5">
                        <p className="text-[#9E9E9E] leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <span className="text-[#2E7D32] font-black text-sm tracking-widest uppercase">Contact</span>
                <h2 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mt-2">
                  {language === 'nl' ? 'Stuur een bericht' : 'Send a message'}
                </h2>
              </div>

              <div className="bg-white rounded-3xl border-2 border-[#E5E5E5] p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black text-[#9E9E9E] tracking-widest uppercase mb-2">
                        {language === 'nl' ? 'Naam' : 'Name'}
                      </label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder={language === 'nl' ? 'Je naam' : 'Your name'}
                        className="h-12 bg-[#F7F7F7] border-0 text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:ring-2 focus:ring-[#2E7D32]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-black text-[#9E9E9E] tracking-widest uppercase mb-2">
                        Email
                      </label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder={language === 'nl' ? 'Je email' : 'Your email'}
                        className="h-12 bg-[#F7F7F7] border-0 text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:ring-2 focus:ring-[#2E7D32]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-black text-[#9E9E9E] tracking-widest uppercase mb-2">
                      {language === 'nl' ? 'Onderwerp' : 'Subject'}
                    </label>
                    <Input
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder={language === 'nl' ? 'Waar gaat het over?' : 'What is it about?'}
                      className="h-12 bg-[#F7F7F7] border-0 text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:ring-2 focus:ring-[#2E7D32]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-[#9E9E9E] tracking-widest uppercase mb-2">
                      {language === 'nl' ? 'Bericht' : 'Message'}
                    </label>
                    <Textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder={language === 'nl' ? 'Vertel ons meer...' : 'Tell us more...'}
                      className="min-h-[120px] bg-[#F7F7F7] border-0 text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:ring-2 focus:ring-[#2E7D32] resize-none"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={sending}
                    className="w-full bg-[#2E7D32] hover:bg-[#256B2B] text-white h-14 font-black text-lg rounded-full"
                  >
                    {sending ? (
                      language === 'nl' ? 'VERSTUREN...' : 'SENDING...'
                    ) : (
                      <>
                        {language === 'nl' ? 'VERSTUUR' : 'SEND'}
                        <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="relative py-20 px-4 overflow-hidden bg-[#2E7D32]">
          <div className="relative max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              {language === 'nl' ? 'Nog geen account?' : 'No account yet?'}
            </h2>
            <p className="text-white/80 text-lg mb-8">
              {language === 'nl' 
                ? 'Onbeperkt swipen. Geen creditcard nodig.'
                : 'Unlimited swipes. No credit card required.'}
            </p>
            <Link to="/swipematch">
              <Button className="bg-white text-[#2E7D32] hover:bg-gray-100 h-14 px-10 font-black text-lg rounded-full">
                {language === 'nl' ? 'START GRATIS' : 'START FREE'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
